/**
 * Pure projection of a Creatomate element's source coordinates into
 * viewport pixels. Split out of `CompositionCanvasActionsLayer` so it
 * stays testable in isolation and free of React / DOM dependencies.
 *
 * Caveats — best-effort projection, NOT a full Creatomate layout solver:
 *   • Assumes the composition fills the canvas (no letterboxing). The
 *     clip preview is already aspect-matched in our flow, so this holds.
 *   • Doesn't apply per-element animations (`animations[]` keyframes) —
 *     uses the static source values. For our use case (anchoring a
 *     floating menu near the selection), the static box is close enough
 *     to the animated rendered position to look right.
 *   • Returns null if the source lacks usable dims; caller falls back
 *     to canvas-top-center.
 */

/**
 * Parse a Creatomate source dimension value as a percentage (0..100).
 * Accepts `"50%"`, `"50"`, `50`. Returns null when undefined / unparseable.
 */
export function parseSourcePercent(value: unknown): number | null {
  if (value == null) return null;
  const str = typeof value === 'number' ? String(value) : String(value);
  const n = parseFloat(str);
  return Number.isFinite(n) ? n : null;
}

export interface ViewportBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function computeElementViewportBox(
  elementSource: Record<string, unknown>,
  videoSource: Record<string, unknown>,
  canvasRect: { left: number; top: number; width: number; height: number },
): ViewportBox | null {
  const compWidth = parseSourcePercent(
    (videoSource as { width?: unknown }).width,
  );
  const compHeight = parseSourcePercent(
    (videoSource as { height?: unknown }).height,
  );
  if (!compWidth || !compHeight) return null;

  const elW =
    parseSourcePercent((elementSource as { width?: unknown }).width) ?? 100;
  const elH =
    parseSourcePercent((elementSource as { height?: unknown }).height) ?? 100;
  const elX =
    parseSourcePercent(
      (elementSource as { x_alignment?: unknown }).x_alignment,
    ) ??
    parseSourcePercent((elementSource as { x?: unknown }).x) ??
    50;
  const elY =
    parseSourcePercent(
      (elementSource as { y_alignment?: unknown }).y_alignment,
    ) ??
    parseSourcePercent((elementSource as { y?: unknown }).y) ??
    50;

  // Convert comp-relative percent to canvas-relative pixels. The x/y
  // are treated as the element's anchor point inside the comp; the
  // anchor is at (elX%, elY%) of the element's own box (matching how
  // Creatomate centers via x_alignment / y_alignment). For our purposes
  // this is good enough — we just need a box near where the user sees
  // the selection.
  const widthPx = (elW / 100) * canvasRect.width;
  const heightPx = (elH / 100) * canvasRect.height;
  const anchorXPx = canvasRect.left + (elX / 100) * canvasRect.width;
  const anchorYPx = canvasRect.top + (elY / 100) * canvasRect.height;
  const left = anchorXPx - widthPx / 2;
  const top = anchorYPx - heightPx / 2;
  return { left, top, width: widthPx, height: heightPx };
}
